import { getCareClockEvidence } from '../../shared/careClockEvidence';
import { getTimelineEntryDate } from '../../services/timeline/dateUtils';

const toTimelineDate = (timestamp) => timestamp?.toDate?.() || new Date(timestamp);

const normalizeTimelineMode = (value) => String(value || '').trim().toLowerCase();

const NON_EXACT_CAPTURE_TIME_SOURCES = new Set([
  '',
  'missing',
  'unknown',
  'blocked_ambiguous_time',
  'blocked_broad_context',
  'blocked_medication_time',
  'inferred',
  'approximate',
]);

const hasMeaningfulClockTime = (date) => (
  date instanceof Date
  && !Number.isNaN(date.getTime())
  && (date.getHours() !== 0 || date.getMinutes() !== 0 || date.getSeconds() !== 0 || date.getMilliseconds() !== 0)
);

const isMidnightTimeValue = (value = '') => {
  const normalized = String(value || '').trim().toLowerCase();
  return normalized === '00:00'
    || normalized === '0:00'
    || normalized === '12:00 am'
    || normalized === '12 am';
};

const hasExplicitMidnightText = (text = '') => (
  /\b(?:midnight|12(?::00)?\s*a\.?m\.?)\b/i.test(String(text || ''))
);

const isDaySummaryEntry = (entry = {}) => {
  const timeSource = normalizeTimelineMode(entry?.timeSource || entry?.metadata?.timeSource);
  const timeBucket = normalizeTimelineMode(entry?.timeBucket || entry?.metadata?.timeBucket);

  return timeSource === 'day_summary' || timeBucket === 'all_day';
};

const isCaptureFirstDateOnlyEntry = (entry = {}) => {
  if (!entry || typeof entry !== 'object') {
    return false;
  }

  const timeSource = normalizeTimelineMode(entry.timeSource || entry.metadata?.timeSource);
  const timestampSource = normalizeTimelineMode(entry.timestampSource || entry.metadata?.timestampSource);
  const parserMode = normalizeTimelineMode(entry.parserMode || entry.metadata?.parserMode);
  const sourceMode = normalizeTimelineMode(entry.sourceMode || entry.metadata?.sourceMode);
  const captureFirst = Boolean(
    entry.metadata?.captureFirst
    || entry.captureFirst
    || parserMode === 'capture_first'
    || sourceMode.includes('capture_first')
    || timestampSource.startsWith('capture-first')
  );
  const hasStoredTime = Boolean(entry.localTime || entry.time || entry.metadata?.localTime || entry.metadata?.time);

  return captureFirst && !hasStoredTime && NON_EXACT_CAPTURE_TIME_SOURCES.has(timeSource);
};

const getTimelineTimestamp = (entryOrTimestamp) => (
  entryOrTimestamp && typeof entryOrTimestamp === 'object' && 'timestamp' in entryOrTimestamp
    ? entryOrTimestamp.timestamp
    : entryOrTimestamp
);

const formatExplicitTimeText = (hourText = '', minuteText = '', periodText = '') => {
  let hours = Number(hourText);
  const minutes = Number(minuteText || 0);
  const period = String(periodText || '').trim().toUpperCase();
  if (!Number.isInteger(hours) || !Number.isInteger(minutes) || minutes < 0 || minutes > 59 || !period) {
    return null;
  }
  if (period === 'PM' && hours < 12) hours += 12;
  if (period === 'AM' && hours === 12) hours = 0;
  if (hours < 0 || hours > 23) return null;

  return new Date(2000, 0, 1, hours, minutes).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getExplicitTimeLabelFromText = (text = '') => {
  const source = String(text || '');
  if (!source || getCareClockEvidence(source).multiple) return null;

  const colonMatch = source.match(/\b(?:around|about|at|by|before|after)?\s*(\d{1,2}):(\d{2})\s*(AM|PM)\b/i);
  if (colonMatch) {
    return formatExplicitTimeText(colonMatch[1], colonMatch[2], colonMatch[3]);
  }

  const meridiemMatch = source.match(/\b(?:around|about|at|by|before|after)\s+(\d{1,2})\s*(AM|PM)\b/i);
  if (meridiemMatch) {
    return formatExplicitTimeText(meridiemMatch[1], '0', meridiemMatch[2]);
  }

  return null;
};

const getEntryTextForTimeFallback = (entry = {}) => [
  entry.text,
  entry.body,
  entry.note,
  entry.summary,
  entry.description,
  entry.sourceText,
  entry.originalText,
].filter(Boolean).join(' ');

const formatTimelineTime = (entryOrTimestamp) => {
  if (isDaySummaryEntry(entryOrTimestamp)) {
    return 'Ongoing';
  }

  if (isCaptureFirstDateOnlyEntry(entryOrTimestamp)) {
    return getExplicitTimeLabelFromText(getEntryTextForTimeFallback(entryOrTimestamp));
  }

  const entryDate = entryOrTimestamp && typeof entryOrTimestamp === 'object' && 'timestamp' in entryOrTimestamp
    ? getTimelineEntryDate(entryOrTimestamp)
    : null;
  if (entryOrTimestamp && typeof entryOrTimestamp === 'object' && 'timestamp' in entryOrTimestamp) {
    const timeSource = normalizeTimelineMode(entryOrTimestamp.timeSource || entryOrTimestamp.metadata?.timeSource);
    const entryText = getEntryTextForTimeFallback(entryOrTimestamp);
    const timeConfidence = normalizeTimelineMode(entryOrTimestamp.timeConfidence || entryOrTimestamp.metadata?.timeConfidence);
    if (!['manual', 'recorded'].includes(timeSource) && getCareClockEvidence(entryText).multiple) return null;
    if (timeConfidence === 'inferred' || timeSource === 'inferred' || timeSource === 'approximate') {
      return null;
    }
    const storedTime = entryOrTimestamp.localTime
      || entryOrTimestamp.time
      || entryOrTimestamp.metadata?.localTime
      || entryOrTimestamp.metadata?.time;
    if (isMidnightTimeValue(storedTime) && !['manual', 'recorded'].includes(timeSource) && !hasExplicitMidnightText(entryText)) {
      return getExplicitTimeLabelFromText(entryText);
    }
    const hasStoredTime = Boolean(
      storedTime
    );
    if (!hasStoredTime && NON_EXACT_CAPTURE_TIME_SOURCES.has(timeSource) && !hasMeaningfulClockTime(entryDate)) {
      return getExplicitTimeLabelFromText(entryText);
    }
  }

  const date = entryDate || toTimelineDate(getTimelineTimestamp(entryOrTimestamp));
  return Number.isNaN(date.getTime())
    ? null
    : `${entryOrTimestamp?.timeSource === 'recorded' ? 'Recorded ' : ''}${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

export { formatTimelineTime, isCaptureFirstDateOnlyEntry, toTimelineDate };
